import React, { useState, useEffect } from 'react';
import AppLayout from '../components/layout/AppLayout';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';
import { CalendarOff, Plus, CheckCircle, XCircle, Clock, Trash2 } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function apiFetch(path, opts = {}) {
  const token = sessionStorage.getItem('itsm_token');
  return fetch(`${API_BASE}${path}`, {
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...opts.headers },
    ...opts,
  }).then(r => r.json());
}

const STATUS_CFG = {
  pending:  { label: 'Pending',  color: '#d97706', bg: '#fef3c7' },
  approved: { label: 'Approved', color: '#16a34a', bg: '#dcfce7' },
  rejected: { label: 'Rejected', color: '#dc2626', bg: '#fee2e2' },
};

function Badge({ status }) {
  const c = STATUS_CFG[status] || STATUS_CFG.pending;
  return (
    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 20, background: c.bg, color: c.color }}>
      {c.label}
    </span>
  );
}

export default function LeavePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [leaves,    setLeaves]    = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');
  const [success,   setSuccess]   = useState('');
  const [showForm,  setShowForm]  = useState(false);
  const [filter,    setFilter]    = useState('');

  const [form, setForm] = useState({ leave_date: '', leave_type: 'full_day', reason: '' });

  const load = async () => {
    setLoading(true);
    try {
      const path = isAdmin ? `/leaves${filter ? `?status=${filter}` : ''}` : '/leaves/my';
      const data = await apiFetch(path);
      setLeaves(Array.isArray(data) ? data : []);
    } catch { setError('Failed to load leaves.'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.leave_date) return;
    setError(''); setSuccess('');
    try {
      const res = await apiFetch('/leaves', { method: 'POST', body: JSON.stringify(form) });
      if (res.error) { setError(res.error); return; }
      setSuccess('Leave request submitted successfully.');
      setShowForm(false);
      setForm({ leave_date: '', leave_type: 'full_day', reason: '' });
      load();
    } catch { setError('Failed to submit leave.'); }
  };

  const handleApprove = async (id) => {
    try {
      const res = await apiFetch(`/leaves/${id}/approve`, { method: 'PATCH', body: '{}' });
      if (res.error) { setError(res.error); return; }
      setSuccess('Leave approved. SLA paused for today if applicable.');
      load();
    } catch { setError('Failed to approve.'); }
  };

  const handleReject = async (id) => {
    try {
      const res = await apiFetch(`/leaves/${id}/reject`, { method: 'PATCH', body: '{}' });
      if (res.error) { setError(res.error); return; }
      setSuccess('Leave rejected.');
      load();
    } catch { setError('Failed to reject.'); }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this leave request?')) return;
    try {
      const res = await apiFetch(`/leaves/${id}`, { method: 'DELETE' });
      if (res.error) { setError(res.error); return; }
      load();
    } catch { setError('Failed to cancel.'); }
  };

  const pendingCount = leaves.filter(l => l.status === 'pending').length;

  return (
    <AppLayout pageTitle="Leave Management">
      <div style={{ maxWidth: 900, margin: '0 auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-main)', margin: 0 }}>
              {isAdmin ? 'Leave Requests' : 'My Leave Requests'}
            </h2>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
              {isAdmin
                ? `Approving leave auto-pauses SLA for the consultant's tickets that day.`
                : 'When leave is approved, your ticket SLA will be paused for that day.'}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {isAdmin && (
              <select
                value={filter}
                onChange={e => setFilter(e.target.value)}
                className="form-control"
                style={{ fontSize: 12, width: 130 }}
              >
                <option value="">All Status</option>
                <option value="pending">Pending {pendingCount > 0 ? `(${pendingCount})` : ''}</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            )}
            {!isAdmin && (
              <button className="btn btn-primary btn-sm" onClick={() => setShowForm(!showForm)} style={{ gap: 6 }}>
                <Plus size={14} /> Apply Leave
              </button>
            )}
          </div>
        </div>

        {error   && <div className="alert alert-error"   style={{ marginBottom: 12 }}>{error}</div>}
        {success && <div className="alert alert-success" style={{ marginBottom: 12 }}>{success}</div>}

        {/* Apply Leave form */}
        {showForm && (
          <div className="card" style={{ padding: '20px 24px', marginBottom: 16, border: '1.5px solid var(--primary)' }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-main)', marginBottom: 14 }}>Apply for Leave</div>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label className="form-label">Leave Date <span style={{ color: '#dc2626' }}>*</span></label>
                  <input
                    type="date" className="form-control"
                    value={form.leave_date}
                    min={new Date().toISOString().split('T')[0]}
                    onChange={e => setForm({ ...form, leave_date: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="form-label">Leave Type</label>
                  <select className="form-control" value={form.leave_type} onChange={e => setForm({ ...form, leave_type: e.target.value })}>
                    <option value="full_day">Full Day</option>
                    <option value="half_day_am">Half Day (AM)</option>
                    <option value="half_day_pm">Half Day (PM)</option>
                  </select>
                </div>
              </div>
              <div style={{ marginBottom: 14 }}>
                <label className="form-label">Reason</label>
                <textarea
                  className="form-control" rows={2}
                  placeholder="Optional reason for leave…"
                  value={form.reason}
                  onChange={e => setForm({ ...form, reason: e.target.value })}
                />
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-outline btn-sm" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary btn-sm" style={{ gap: 6 }}>
                  <CalendarOff size={13} /> Submit Request
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Leave list */}
        <div className="table-wrap">
          {loading ? (
            <div className="flex-center" style={{ height: 140 }}>
              <div className="spinner" style={{ width: 30, height: 30 }} />
            </div>
          ) : leaves.length === 0 ? (
            <div className="empty-state">
              <CalendarOff size={36} color="var(--gray-300)" style={{ marginBottom: 10 }} />
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-muted)' }}>No leave requests</div>
              {!isAdmin && (
                <button className="btn btn-primary btn-sm" onClick={() => setShowForm(true)} style={{ marginTop: 12, gap: 6 }}>
                  <Plus size={13} /> Apply for Leave
                </button>
              )}
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  {isAdmin && <th>Consultant</th>}
                  <th>Date</th>
                  <th>Type</th>
                  <th>Reason</th>
                  <th>Status</th>
                  {isAdmin && <th>Approved By</th>}
                  <th>Applied On</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {leaves.map(l => (
                  <tr key={l.id}>
                    {isAdmin && (
                      <td>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{l.consultant_name}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{l.consultant_email}</div>
                      </td>
                    )}
                    <td style={{ fontWeight: 600, fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                      {format(new Date(l.leave_date), 'dd MMM yyyy')}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-sub)' }}>
                      {{ full_day: 'Full Day', half_day_am: 'Half Day (AM)', half_day_pm: 'Half Day (PM)' }[l.leave_type] || l.leave_type}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-muted)', maxWidth: 200 }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {l.reason || <span style={{ fontStyle: 'italic' }}>—</span>}
                      </div>
                    </td>
                    <td><Badge status={l.status} /></td>
                    {isAdmin && (
                      <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        {l.approved_by_name || '—'}
                      </td>
                    )}
                    <td style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                      {format(new Date(l.created_at), 'dd MMM yyyy')}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        {isAdmin && l.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleApprove(l.id)}
                              style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 6, border: '1px solid #86efac', background: '#f0fdf4', color: '#16a34a', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
                            >
                              <CheckCircle size={12} /> Approve
                            </button>
                            <button
                              onClick={() => handleReject(l.id)}
                              style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 6, border: '1px solid #fca5a5', background: '#fff5f5', color: '#dc2626', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
                            >
                              <XCircle size={12} /> Reject
                            </button>
                          </>
                        )}
                        {!isAdmin && l.status === 'pending' && (
                          <button
                            onClick={() => handleCancel(l.id)}
                            style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-muted)', fontSize: 11, cursor: 'pointer' }}
                          >
                            <Trash2 size={12} /> Cancel
                          </button>
                        )}
                        {l.status !== 'pending' && (
                          <span style={{ fontSize: 11, color: 'var(--text-muted)', padding: '4px 0' }}>
                            {l.approved_at ? format(new Date(l.approved_at), 'dd MMM') : '—'}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Info card */}
        <div className="card" style={{ padding: '14px 18px', marginTop: 16, background: 'var(--primary-50, #f5f3ff)', border: '1px solid var(--primary-light)' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--primary)', marginBottom: 8 }}>SLA Pause Rules</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
            {[
              { icon: '🕘', text: 'Business hours: 9:00 AM – 6:00 PM, Mon–Fri' },
              { icon: '⏸️', text: 'SLA pauses automatically on Check-Out' },
              { icon: '▶️', text: 'SLA resumes automatically on Check-In' },
              { icon: '📅', text: 'Approved leave pauses SLA for that day' },
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12, color: 'var(--text-sub)' }}>
                <span>{item.icon}</span>
                <span>{item.text}</span>
              </div>
            ))}
          </div>
        </div>

      </div>
    </AppLayout>
  );
}
